/* Shared styles & components for all OData list pages */
import { useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridPreferencePanelsValue,
  useGridApiContext,
} from '@mui/x-data-grid';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const s = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    height: '100%',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  pageHeaderText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  pageTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 800,
    fontFamily: "'Sora', 'Manrope', sans-serif",
    color: 'var(--text-primary, #111827)',
  },
  pageSubtitle: {
    margin: 0,
    fontSize: 13,
    color: 'var(--text-secondary, #4b5563)',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e5e7eb)',
    borderBottom: 0,
    borderRadius: '16px 16px 0 0',
    gap: 8,
    flexWrap: 'wrap',
  },
  toolbarLeft: { display: 'flex', gap: 8, alignItems: 'center' },
  toolbarRight: { display: 'flex', gap: 8, alignItems: 'center' },
  filterInput: {
    padding: '7px 12px',
    borderRadius: 12,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--input-bg, #ffffff)',
    color: 'var(--text-primary, #1f2937)',
    fontSize: 14,
    outline: 'none',
    width: 220,
  },
  tableWrapper: {
    background: 'var(--grid-canvas, #eef2f9)',
    overflow: 'auto',
    position: 'relative',
    flexGrow: 1,
    minHeight: 520,
    padding: '6px 12px',
  },
  gridCard: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: '0 0 16px 16px',
    boxShadow: '0 16px 30px -10px rgba(15, 23, 42, 0.12)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 700,
    color: 'var(--text-secondary, #4b5563)',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: '2px solid var(--border-color, #e5e7eb)',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    background: 'var(--bg-card, #ffffff)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  td: {
    padding: '11px 16px',
    borderBottom: '1px solid var(--border-color, #e5e7eb)',
    color: 'var(--text-primary, #111827)',
  },
  tdMuted: {
    padding: '11px 16px',
    borderBottom: '1px solid var(--border-color, #e5e7eb)',
    color: 'var(--text-primary, #111827)',
    fontSize: 13,
  },
  tdActions: {
    padding: '11px 16px',
    borderBottom: '1px solid var(--border-color, #e5e7eb)',
    color: 'var(--text-primary, #1f2937)',
    whiteSpace: 'nowrap',
  },
  tr: { cursor: 'pointer' },
  trSelected: {
    cursor: 'pointer',
    background: 'rgba(0, 115, 234, 0.07)',
    outline: '1px solid rgba(0, 115, 234, 0.18)',
    outlineOffset: -1,
  },
  emptyCell: {
    textAlign: 'center',
    padding: '3rem',
    color: 'var(--text-secondary, #4b5563)',
  },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    borderTop: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-card, #ffffff)',
    flexWrap: 'wrap',
    gap: 8,
  },
  pageInfo: {
    fontSize: 13,
    color: 'var(--text-primary, #111827)',
    fontWeight: 400,
  },
  pageControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  pageBtn: {
    padding: '5px 10px',
    borderRadius: 10,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--input-bg, #ffffff)',
    color: 'var(--text-primary, #111827)',
    cursor: 'pointer',
    fontSize: 14,
    minWidth: 32,
    textAlign: 'center',
    fontWeight: 400,
  },
  pageBtnDisabled: {
    padding: '5px 10px',
    borderRadius: 10,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--input-bg, #f9fafb)',
    color: 'var(--text-secondary, #4b5563)',
    cursor: 'not-allowed',
    fontSize: 14,
    minWidth: 32,
    textAlign: 'center',
    opacity: 0.85,
    fontWeight: 400,
  },
  pageCurrent: {
    padding: '5px 10px',
    borderRadius: 10,
    border: '1px solid var(--accent)',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 14,
    minWidth: 32,
    textAlign: 'center',
    fontWeight: 600,
  },
  pageSizeSelect: {
    padding: '5px 8px',
    borderRadius: 10,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--input-bg, #ffffff)',
    color: 'var(--text-primary, #111827)',
    fontSize: 13,
    marginLeft: 8,
    outline: 'none',
    fontWeight: 400,
  },
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--loading-overlay-bg, rgba(255,255,255,0.55))',
    zIndex: 5,
    fontSize: 15,
    color: 'var(--text-secondary, #4b5563)',
    gap: 8,
  },
  sortIcon: { marginLeft: 4, fontSize: 11, color: 'var(--text-secondary, #4b5563)' },

  /* Buttons */
  btnPrimary: {
    padding: '8px 16px',
    borderRadius: 14,
    border: '1px solid rgba(59, 130, 246, 0.26)',
    background: 'var(--btn-gradient, linear-gradient(135deg, #0073ea 0%, #0ea5e9 100%))',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 14,
    boxShadow: 'var(--btn-shadow, 0 12px 26px rgba(0, 115, 234, 0.28))',
  },
  btnSecondary: {
    padding: '7px 16px',
    borderRadius: 14,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--input-bg, #ffffff)',
    color: 'var(--text-primary, #1f2937)',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: 14,
  },
  btnDanger: {
    padding: '7px 16px',
    borderRadius: 14,
    border: 'none',
    background: 'var(--danger, #e2445c)',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 14,
  },
  btnDisabled: {
    padding: '7px 16px',
    borderRadius: 14,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--input-bg, #f9fafb)',
    color: 'var(--text-secondary, #6b7280)',
    cursor: 'not-allowed',
    fontWeight: 500,
    fontSize: 14,
    opacity: 0.45,
  },
  btnSmallInfo: {
    padding: '5px 10px',
    borderRadius: 10,
    border: 'none',
    background: 'var(--c-info, #0073ea)',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 12,
    marginRight: 6,
  },
  btnSmallWarning: {
    padding: '5px 10px',
    borderRadius: 10,
    border: 'none',
    background: 'var(--c-edit, #fdab3d)',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 12,
    marginRight: 6,
  },
  btnSmallDanger: {
    padding: '5px 10px',
    borderRadius: 10,
    border: 'none',
    background: 'var(--c-danger, #e2445c)',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 12,
  },
  statusActive: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    border: '1px solid rgba(0, 200, 117, 0.30)',
    background: 'rgba(0, 200, 117, 0.12)',
    color: '#0f5132',
    fontWeight: 800,
    fontSize: 12,
  },
  statusPassive: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    border: '1px solid rgba(100, 116, 139, 0.28)',
    background: 'rgba(100, 116, 139, 0.14)',
    color: 'var(--text-secondary, #64748b)',
    fontWeight: 800,
    fontSize: 12,
  },

  /* Modal */
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'var(--bg-card, #ffffff)',
    borderRadius: 18,
    width: '100%',
    maxWidth: 520,
    boxShadow: 'var(--shadow-pop, 0 18px 44px rgba(15, 23, 42, 0.18))',
    animation: 'panel-in 220ms ease both',
    color: 'var(--text-primary, #1f2937)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px 16px',
    borderBottom: '1px solid var(--border-color, #e5e7eb)',
  },
  modalTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 800,
    color: 'var(--text-primary, #111827)',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: 26,
    cursor: 'pointer',
    color: 'var(--text-secondary, #4b5563)',
    lineHeight: 1,
    padding: '0 4px',
  },
  modalBody: {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    maxHeight: '65vh',
    overflowY: 'auto',
  },
  modalFooter: {
    display: 'flex',
    gap: 10,
    padding: '16px 24px 20px',
    justifyContent: 'flex-end',
    borderTop: '1px solid var(--border-color, #e5e7eb)',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
  },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #374151)' },
  input: {
    padding: '9px 13px',
    borderRadius: 12,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--input-bg, #ffffff)',
    color: 'var(--text-primary, #1f2937)',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputView: {
    padding: '9px 13px',
    borderRadius: 12,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--bg-card, #f9fafb)',
    color: 'var(--text-secondary, #4b5563)',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    cursor: 'default',
  },
  select: {
    padding: '9px 13px',
    borderRadius: 12,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--input-bg, #ffffff)',
    color: 'var(--text-primary, #1f2937)',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  selectView: {
    padding: '9px 13px',
    borderRadius: 12,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--bg-card, #f9fafb)',
    color: 'var(--text-secondary, #4b5563)',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  textarea: {
    padding: '9px 13px',
    borderRadius: 12,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--input-bg, #ffffff)',
    color: 'var(--text-primary, #1f2937)',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  textareaView: {
    padding: '9px 13px',
    borderRadius: 12,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--bg-card, #f9fafb)',
    color: 'var(--text-secondary, #4b5563)',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
  },

  /* Toast */
  toast: {
    position: 'fixed',
    bottom: 32,
    right: 32,
    padding: '14px 22px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    zIndex: 2000,
    boxShadow: '0 4px 24px rgba(0,0,0,0.22)',
    display: 'flex',
    alignItems: 'center',
    minWidth: 260,
    animation: 'toastIn 0.3s ease',
  },
  toastSuccess: { background: '#16a34a', color: '#fff' },
  toastError: { background: '#dc2626', color: '#fff' },
};

export const pageSizeOptions = [10, 20, 50, 100];

export const newRecordButtonStyle = {
  ...s.btnPrimary,
};

export const exportButtonStyle = {
  padding: '6px 8px',
  borderRadius: 6,
  border: 'none',
  background: 'transparent',
  color: 'var(--text-secondary, #334155)',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 12,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
};

const toolbarSectionDividerStyle = {
  width: 1,
  height: 20,
  background: 'var(--border-color, #dbe2ea)',
};

function ToolbarIconButton({ title, onClick, children }) {
  return (
    <IconButton
      size="small"
      onClick={onClick}
      title={title}
      aria-label={title}
      sx={{
        width: 32,
        height: 32,
        borderRadius: '6px',
        color: 'var(--text-secondary, #4b5563)',
      }}
    >
      {children}
    </IconButton>
  );
}

function ColumnsIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="3" y="5" width="5" height="14" fill="currentColor" />
      <rect x="10" y="5" width="5" height="14" fill="currentColor" />
      <rect x="17" y="5" width="4" height="14" fill="currentColor" />
    </svg>
  );
}

function FilterIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M4 6h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M7 12h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M10 18h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function DensityIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M5 7h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M5 17h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function ExcelIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 4v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8.5 10.5L12 14l3.5-3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M5 18h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function exportToExcel(rows, columns, fileName) {
  let data;
  let headers;
  if (columns) {
    const visibleCols = columns.filter(c => c.field !== 'actions' && c.field !== '__check__');
    headers = visibleCols.map(c => c.headerName || c.field);
    data = rows.map(row =>
      visibleCols.reduce((acc, col) => {
        const header = col.headerName || col.field;
        const val = col.valueGetter ? col.valueGetter({ row }) : row[col.field];
        acc[header] = val ?? '';
        return acc;
      }, {})
    );
  } else {
    data = rows;
    headers = data.length ? Object.keys(data[0]) : [];
  }

  // ── Gelişmiş biçim: sayısal kolon tespiti → sayı formatı + TOPLAM satırı + kolon genişliği.
  // (SheetJS Community kalın/renk hücre stili DESTEKLEMEZ; genişlik `!cols` ve sayı formatı `z`
  //  desteklenir — bunları kullanıyoruz.)
  const numericByHeader = {};
  headers.forEach((h) => {
    const vals = data.map((r) => r[h]).filter((v) => v !== '' && v !== null && v !== undefined);
    numericByHeader[h] = vals.length > 0 && vals.every((v) => typeof v === 'number' || (!Number.isNaN(Number(v)) && String(v).trim() !== ''));
  });

  // Sayısal hücreleri gerçek sayıya çevir (Excel'de sayı olarak dursun).
  const normalized = data.map((r) => {
    const o = { ...r };
    headers.forEach((h) => { if (numericByHeader[h] && o[h] !== '' && o[h] != null) o[h] = Number(o[h]); });
    return o;
  });

  // TOPLAM satırı (sayısal kolonların toplamı).
  const anyNumeric = headers.some((h) => numericByHeader[h]);
  if (anyNumeric && normalized.length > 0) {
    const totalRow = {};
    headers.forEach((h, i) => {
      if (i === 0) totalRow[h] = 'TOPLAM';
      else if (numericByHeader[h]) totalRow[h] = normalized.reduce((s, r) => s + (Number(r[h]) || 0), 0);
      else totalRow[h] = '';
    });
    normalized.push(totalRow);
  }

  const ws = XLSX.utils.json_to_sheet(normalized, { header: headers });

  // Kolon genişlikleri (başlık + içerik uzunluğuna göre).
  ws['!cols'] = headers.map((h) => {
    const maxLen = Math.max(String(h).length, ...normalized.map((r) => String(r[h] ?? '').length));
    return { wch: Math.min(40, Math.max(10, maxLen + 2)) };
  });

  // Sayısal hücrelere binlik ayraçlı format.
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let c = range.s.c; c <= range.e.c; c += 1) {
    if (!numericByHeader[headers[c]]) continue;
    for (let r = range.s.r + 1; r <= range.e.r; r += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.t === 'n') cell.z = '#,##0.##';
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${fileName || 'export'}.xlsx`);
}

export function ExportExcelButton({ rows, columns, fileName }) {
  return (
    <button
      type="button"
      style={exportButtonStyle}
      onClick={() => exportToExcel(rows, columns, fileName)}
      title="Excel'e Aktar"
      aria-label="Excel'e Aktar"
    >
      <ExcelIcon />
    </button>
  );
}

export const gridActionButton = {
  border: 'none',
  borderRadius: 10,
  color: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  height: 30,
  lineHeight: '30px',
  padding: '0 10px',
  minWidth: 74,
};

/* Fronta-style status pill (ok / warn / bad / neutral) */
const statusPillBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 999,
  fontWeight: 800,
  fontSize: 12,
  lineHeight: 1.4,
  border: '1px solid transparent',
  whiteSpace: 'nowrap',
};

const statusPillTones = {
  ok: { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' },
  warn: { color: '#92400e', background: 'rgba(253, 171, 61, 0.14)', borderColor: 'rgba(253, 171, 61, 0.32)' },
  bad: { color: '#7f1d1d', background: 'rgba(226, 68, 92, 0.12)', borderColor: 'rgba(226, 68, 92, 0.30)' },
  neutral: { color: 'var(--text-secondary, #64748b)', background: 'rgba(100, 116, 139, 0.14)', borderColor: 'rgba(100, 116, 139, 0.28)' },
};

export function statusPill(value, { activeLabel = 'Aktif', passiveLabel = 'Pasif' } = {}) {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  let tone = 'neutral';
  let label = String(value);
  if (value === true || num === 1 || value === 'Aktif' || value === 'active') {
    tone = 'ok';
    label = activeLabel;
  } else if (value === false || num === 0 || value === 'Pasif' || value === 'passive') {
    tone = 'neutral';
    label = passiveLabel;
  }
  return (
    <span style={{ ...statusPillBase, ...statusPillTones[tone] }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor', opacity: 0.85, flexShrink: 0 }} />
      {label}
    </span>
  );
}

function ExcelExportToolbarButton({ fileName }) {
  const apiRef = useGridApiContext();
  const handleExport = () => {
    const colDefs = apiRef.current.getAllColumns().filter(c => c.field !== '__check__' && c.field !== 'actions');
    const rowModels = apiRef.current.getSortedRows();
    exportToExcel(rowModels, colDefs, fileName || 'export');
  };
  return (
    <ToolbarIconButton title="Export" onClick={handleExport}>
      <ExcelIcon size={16} />
    </ToolbarIconButton>
  );
}

export function CustomGridToolbar({ fileName }) {
  const apiRef = useGridApiContext();
  const [densityAnchorEl, setDensityAnchorEl] = useState(null);

  return (
    <GridToolbarContainer
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        padding: '8px 12px',
      }}
    >
      <div />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <ToolbarIconButton
          title="Columns"
          onClick={() => apiRef.current.showPreferences(GridPreferencePanelsValue.columns)}
        >
          <ColumnsIcon />
        </ToolbarIconButton>
        <ToolbarIconButton
          title="Filters"
          onClick={() => apiRef.current.showPreferences(GridPreferencePanelsValue.filters)}
        >
          <FilterIcon />
        </ToolbarIconButton>
        <ToolbarIconButton
          title="Density"
          onClick={(event) => setDensityAnchorEl(event.currentTarget)}
        >
          <DensityIcon />
        </ToolbarIconButton>
        <Menu
          anchorEl={densityAnchorEl}
          open={Boolean(densityAnchorEl)}
          onClose={() => setDensityAnchorEl(null)}
        >
          <MenuItem onClick={() => { apiRef.current.setDensity('compact'); setDensityAnchorEl(null); }}>Compact</MenuItem>
          <MenuItem onClick={() => { apiRef.current.setDensity('standard'); setDensityAnchorEl(null); }}>Standard</MenuItem>
          <MenuItem onClick={() => { apiRef.current.setDensity('comfortable'); setDensityAnchorEl(null); }}>Comfortable</MenuItem>
        </Menu>
        <div style={toolbarSectionDividerStyle} />
        <ExcelExportToolbarButton fileName={fileName} />
      </div>
    </GridToolbarContainer>
  );
}

export function toGridSortField(field) {
  if (!field) return field;
  return field.charAt(0).toLowerCase() + field.slice(1);
}

export function toODataSortField(field) {
  if (!field) return field;
  return field.charAt(0).toUpperCase() + field.slice(1);
}

export const standardGridSx = {
  border: 0,
  backgroundColor: 'transparent',
  color: 'var(--text-primary, #111827)',
  fontFamily: 'inherit',
  '--DataGrid-rowBorderColor': 'transparent',
  '& .MuiDataGrid-main': {
    backgroundColor: 'transparent',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'transparent',
    borderBottom: 0,
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: 'transparent',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: 'var(--text-secondary, #4b5563)',
    fontWeight: 800,
    fontSize: 11.5,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-cell': {
    border: 0,
    color: 'var(--text-primary, #111827)',
    fontSize: 13.5,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-row': {
    cursor: 'pointer',
    backgroundColor: 'var(--row-card-bg, #ffffff)',
    borderRadius: '12px',
    boxShadow: '0 6px 18px rgba(15, 23, 42, 0.10)',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'var(--row-card-hover, #f1f5f9)',
    boxShadow: '0 10px 22px rgba(15, 23, 42, 0.10)',
  },
  '& .standard-grid-row-selected': {
    backgroundColor: 'var(--accent-soft, rgba(0, 115, 234, 0.12)) !important',
    boxShadow: '0 0 0 1.5px var(--accent, #0073ea)',
  },
  '& .MuiDataGrid-filler': {
    backgroundColor: 'transparent',
  },
  '& .MuiDataGrid-scrollbarFiller': {
    backgroundColor: 'transparent',
  },
  '& .MuiDataGrid-footerContainer': {
    display: 'none',
  },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
    color: 'var(--text-primary, #111827)',
    fontWeight: 400,
  },
  '& .MuiIconButton-root': {
    color: 'var(--text-primary, #111827)',
  },
  '& .MuiDataGrid-toolbarContainer': {
    backgroundColor: 'transparent',
    borderBottom: '1px solid var(--border-color, #e5e7eb)',
  },
  '& .MuiDataGrid-toolbarContainer button': {
    color: 'var(--text-secondary, #334155)',
  },
  '& .MuiDataGrid-overlay': {
    backgroundColor: 'var(--loading-overlay-bg, rgba(255,255,255,0.55))',
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-menuIcon button': {
    color: 'var(--text-secondary, #4b5563)',
  },
  '& .MuiDataGrid-sortIcon': {
    color: 'var(--text-secondary, #4b5563)',
    opacity: 1,
  },
  '& .MuiDataGrid-iconButtonContainer': {
    visibility: 'visible',
    width: 'auto',
  },
  '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
    backgroundColor: 'transparent',
    color: 'var(--text-secondary, #4b5563)',
    padding: 2,
  },
  '& .MuiDataGrid-columnHeader .MuiIconButton-root:hover': {
    backgroundColor: 'transparent',
  },
};

const gridRowSpacing = () => ({ top: 4, bottom: 4 });

export function StandardDataGrid({
  rows,
  columns,
  totalCount,
  loading,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
  sortField,
  sortDir,
  onSortChange,
  selectedId,
  onRowClick,
  onRowDoubleClick,
  exportFileName,
  rowHeight = 60,
  getRowHeight,
}) {
  const rowCount = rows?.length ?? 0;
  const inferredTotalCount = totalCount > 0
    ? totalCount
    : Math.max(0, currentPage - 1) * pageSize + rowCount;
  const totalPages = Math.max(1, Math.ceil(inferredTotalCount / pageSize));
  const pageFrom = rowCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageTo = rowCount === 0 ? 0 : pageFrom + rowCount - 1;
  const sortModel = sortField ? [{ field: toGridSortField(sortField), sort: sortDir }] : [];

  return (
    <div style={s.gridCard}>
      <div style={s.tableWrapper}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={rowHeight}
          getRowHeight={getRowHeight}
          getRowSpacing={gridRowSpacing}
          columnHeaderHeight={46}
          hideFooter
          showToolbar
          slots={{ toolbar: CustomGridToolbar }}
          slotProps={{ toolbar: { fileName: exportFileName } }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            const next = model[0];
            if (!next) {
              onSortChange(sortField, 'asc');
              return;
            }
            onSortChange(toODataSortField(next.field), next.sort || 'asc');
          }}
          onRowClick={(params) => onRowClick?.(params.row)}
          onRowDoubleClick={(params) => onRowDoubleClick?.(params.row)}
          getRowClassName={(params) => selectedId === params.id ? 'standard-grid-row-selected' : ''}
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: 'Kayit bulunamadi' }}
          sx={standardGridSx}
        />
      </div>

      <div style={s.paginationBar}>
        <div style={s.pageInfo}>
          {pageFrom}-{pageTo} / <span>{inferredTotalCount}</span> kayit
          {rowCount > 0 && <span style={{ marginLeft: 4 }}>(Sayfa {currentPage} / {totalPages})</span>}
        </div>
        <div style={s.pageControls}>
          <button
            type="button"
            style={currentPage === 1 ? s.pageBtnDisabled : s.pageBtn}
            disabled={currentPage === 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          >
            &lsaquo;
          </button>
          <button
            type="button"
            style={currentPage >= totalPages ? s.pageBtnDisabled : s.pageBtn}
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          >
            &rsaquo;
          </button>
          <select
            style={s.pageSizeSelect}
            value={pageSize}
            onChange={event => onPageSizeChange(Number(event.target.value))}
          >
            {pageSizeOptions.map(size => <option key={size} value={size}>{size} / sayfa</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

/* ── Shared helper functions ─────────────────────────────────────────────── */
export function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export function buildODataUrl(base, { pageSize, currentPage, sortField, sortDir, filterParams, expand, select }) {
  const skip = (currentPage - 1) * pageSize;
  let url = `${base}?$count=true&$top=${pageSize}&$skip=${skip}`;
  if (filterParams) url += `&$filter=${encodeURIComponent(filterParams)}`;
  if (expand) url += `&$expand=${encodeURIComponent(expand)}`;
  // $select: yalnız istenen kolonları çek (payload + serileştirme düşer). Backend camelCase
  // döndürür. select verilmezse tüm entity döner (geriye dönük uyumlu).
  if (select && select.length) url += `&$select=${encodeURIComponent(select.join(','))}`;
  url += `&$orderby=${encodeURIComponent(`${sortField} ${sortDir}`)}`;
  return url;
}

export function buildODataCountUrl(base, { filterParams, expand } = {}) {
  let url = `${base}?$count=true&$top=0`;
  if (filterParams) url += `&$filter=${encodeURIComponent(filterParams)}`;
  if (expand) url += `&$expand=${encodeURIComponent(expand)}`;
  return url;
}

export function buildODataScanUrl(base, { pageSize, skip = 0, filterParams, expand } = {}) {
  let url = `${base}?$top=${pageSize}&$skip=${skip}`;
  if (filterParams) url += `&$filter=${encodeURIComponent(filterParams)}`;
  if (expand) url += `&$expand=${encodeURIComponent(expand)}`;
  return url;
}

export function getODataRows(data) {
  return data?.value ?? data?.data ?? data?.Data ?? (Array.isArray(data) ? data : []);
}

export function getExplicitODataTotalCount(data) {
  const exactCount = data?.['@odata.count'] ?? data?.['@OData.Count'] ?? data?.count ?? data?.Count;
  const parsedExactCount = typeof exactCount === 'string' ? Number(exactCount) : exactCount;
  return Number.isFinite(parsedExactCount) ? parsedExactCount : null;
}

function tryParseNumericText(text) {
  const normalized = typeof text === 'string' ? text.trim() : '';
  if (!normalized) return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

async function readCountResponse(response) {
  const raw = await response.text();
  const directCount = tryParseNumericText(raw);
  if (directCount != null) return directCount;

  try {
    return getExplicitODataTotalCount(JSON.parse(raw));
  } catch {
    return null;
  }
}

async function scanODataTotalCount({ base, filterParams, expand, headers }) {
  const batchSize = 1000;
  let total = 0;
  let skip = 0;

  for (let attempt = 0; attempt < 1000; attempt += 1) {
    const response = await fetch(buildODataScanUrl(base, { pageSize: batchSize, skip, filterParams, expand }), { headers });
    if (!response.ok) {
      return null;
    }

    const payload = await response.json();
    const rows = getODataRows(payload);
    total += rows.length;

    const explicitCount = getExplicitODataTotalCount(payload);
    if (explicitCount != null) {
      return explicitCount;
    }

    if (rows.length < batchSize) {
      return total;
    }

    skip += batchSize;
  }

  return total;
}

export function getODataTotalCount(data, rows, currentPage, pageSize) {
  const explicitCount = getExplicitODataTotalCount(data);
  if (explicitCount != null) return explicitCount;

  const rowCount = rows?.length ?? 0;
  const loadedUntil = Math.max(0, currentPage - 1) * pageSize + rowCount;

  // Some /api endpoints apply $top/$skip but serialize a plain array without @odata.count.
  // In that case keep the next page enabled while a full page is returned.
  return loadedUntil + (rowCount === pageSize ? 1 : 0);
}

export async function resolveODataTotalCount({
  data,
  rows,
  currentPage,
  pageSize,
  base,
  filterParams,
  expand,
  headers,
}) {
  const exactCount = getODataTotalCount(data, rows, currentPage, pageSize);
  const rowCount = rows?.length ?? 0;
  const heuristicCount = Math.max(0, currentPage - 1) * pageSize + rowCount + (rowCount === pageSize ? 1 : 0);

  if (exactCount !== heuristicCount || !base) {
    return exactCount;
  }

  try {
    const response = await fetch(buildODataCountUrl(base, { filterParams, expand }), { headers });
    if (!response.ok) {
      const scannedCount = await scanODataTotalCount({ base, filterParams, expand, headers });
      return scannedCount ?? exactCount;
    }

    const resolvedCount = await readCountResponse(response);
    if (resolvedCount != null) {
      return resolvedCount;
    }

    const scannedCount = await scanODataTotalCount({ base, filterParams, expand, headers });
    return scannedCount ?? exactCount;
  } catch {
    return exactCount;
  }
}

/* ── Pagination Bar Component ────────────────────────────────────────────── */
export function Pagination({ currentPage, totalCount, pageSize, onPageChange, onPageSizeChange }) {
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const skip = (currentPage - 1) * pageSize;
  const from = totalCount === 0 ? 0 : skip + 1;
  const to = Math.min(skip + pageSize, totalCount);

  const pages = buildPageNumbers(currentPage, totalPages);

  return (
    <div style={s.paginationBar}>
      <div style={s.pageInfo}>
        {totalCount > 0
          ? <>{from}–{to} / <span>{totalCount}</span> kayıt</>
          : '0 kayıt'}
        {totalCount > 0 && <span style={{ marginLeft: 4 }}>(Sayfa {currentPage} / {totalPages})</span>}
      </div>
      <div style={s.pageControls}>
        <button
          type="button"
          style={currentPage === 1 ? s.pageBtnDisabled : s.pageBtn}
          disabled={currentPage === 1}
          onClick={() => onPageChange(1)}
        >«</button>
        <button
          type="button"
          style={currentPage === 1 ? s.pageBtnDisabled : s.pageBtn}
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >‹</button>

        {pages.map((p, i) =>
          p === '...'
            ? <span key={`e${i}`} style={{ padding: '5px 4px', color: 'var(--text-primary, #111827)', fontWeight: 400 }}>…</span>
            : <button
                type="button"
                key={p}
                style={p === currentPage ? s.pageCurrent : s.pageBtn}
                onClick={() => onPageChange(p)}
              >{p}</button>
        )}

        <button
          type="button"
          style={currentPage >= totalPages ? s.pageBtnDisabled : s.pageBtn}
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >›</button>
        <button
          type="button"
          style={currentPage >= totalPages ? s.pageBtnDisabled : s.pageBtn}
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(totalPages)}
        >»</button>

        <select
          style={s.pageSizeSelect}
          value={pageSize}
          onChange={e => onPageSizeChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / sayfa</option>)}
        </select>
      </div>
    </div>
  );
}

function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  if (current <= 4) {
    pages.push(1, 2, 3, 4, 5, '...', total);
  } else if (current >= total - 3) {
    pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '...', current - 1, current, current + 1, '...', total);
  }
  return pages;
}

/* ── Toast Component ─────────────────────────────────────────────────────── */
export function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div style={{ ...s.toast, ...(toast.type === 'success' ? s.toastSuccess : s.toastError) }}>
      {toast.type === 'success' ? '✓' : '✗'}&nbsp;&nbsp;{toast.message}
    </div>
  );
}
